import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { SignalRHubService } from './signalr-hub.service';
import { UserService } from './user.service';
export interface FormPresenceViewer {
	userId: string;
	firstName: string;
	lastName: string;
	mode: 'view' | 'edit';
}
const HEARTBEAT_INTERVAL_MS = 60_000;

@Injectable({
	providedIn: 'root',
})
export class FormPresenceService implements OnDestroy {
	private urlPath = '';
	public readonly clientId: string;
	private readonly viewers$ = new BehaviorSubject<FormPresenceViewer[]>([]);
	private currentFormType: string | null = null;
	private currentFormId: string | null = null;
	private currentClientId: string | null = null;
	private currentMode: 'view' | 'edit' = 'view';
	private hubSubscription: Subscription | null = null;
	private heartbeatSubscription: Subscription | null = null;
	constructor(private http: HttpClient, private userService: UserService, private signalRHub: SignalRHubService) {
		this.clientId = this.userService.currentUserValue.user.clientId;
		this.urlPath = `${environment.apiUrl}form-presence/clients/${this.clientId}/`;
	}
	get viewers(): Observable<FormPresenceViewer[]> {
		return this.viewers$.asObservable();
	}
	joinForm(clientId: string, formType: string, formId: string, mode: 'view' | 'edit'): void {
		if (!formId) return;

		this.currentClientId = clientId;
		this.currentFormType = formType;
		this.currentFormId = formId;
		this.currentMode = mode;
		this.refreshViewers();
		this.signalRHub.on('formViewersChanged', () => {
			this.refreshViewers();
		});
		const doJoin = () => {
			this.signalRHub.send('JoinForm', formType, formId, mode).catch(() => {});
			this.refreshViewers();
			this.startHeartbeat();
		};
		this.hubSubscription = this.signalRHub.whenConnected().subscribe(() => {
			doJoin();
		});
	}
	leaveForm(): void {
		if (!this.currentFormType || !this.currentFormId) return;
		this.signalRHub.send('LeaveForm', this.currentFormType, this.currentFormId).catch(() => {});
		this.stopHeartbeat();
		this.viewers$.next([]);
		this.currentFormType = null;
		this.currentFormId = null;
		this.currentClientId = null;
		this.hubSubscription?.unsubscribe();
		this.hubSubscription = null;
	}
	private refreshViewers(): void {
		if (!this.currentClientId || !this.currentFormType || !this.currentFormId) return;
		const url = `${this.urlPath}${this.currentFormType}/${this.currentFormId}`;
		this.http.get<FormPresenceViewer[]>(url).subscribe({
			next: (viewers) => {
				this.viewers$.next(viewers);
			},
			error: (err) => {
				console.error('Failed to refresh form viewers:', err);
			},
		});
	}
	private startHeartbeat(): void {
		this.stopHeartbeat();
		this.heartbeatSubscription = interval(HEARTBEAT_INTERVAL_MS).subscribe(() => {
			if (this.currentFormType && this.currentFormId && this.signalRHub.isConnected) {
				this.signalRHub.send('JoinForm', this.currentFormType, this.currentFormId, this.currentMode).catch(() => {});
			}
		});
	}
	private stopHeartbeat(): void {
		this.heartbeatSubscription?.unsubscribe();
		this.heartbeatSubscription = null;
	}
	ngOnDestroy(): void {
		this.leaveForm();
	}
}
